import { useTranslation } from 'react-i18next';
import { ICellRendererParams } from 'ag-grid-community';
import { z } from 'zod';

import { getSVGIconNameFromDocument } from '../../../utils/getSVGIconNameFromDocument';
import { SVGIcon } from '../../icons/SVGIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';
import { EMPTY_VALUE } from '../valueFormatters';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './fileDownloadCellRenderer.module.scss';

export const FileDto = z.object({
  id: z.string(),
  fileName: z.string(),
  downloadToken: z.string().optional(),
  mimetype: z.string(),
  virusFree: z.boolean().optional(),
  parentId: z.string().optional(),
});

const Files = z.array(FileDto);

type FileDto = z.infer<typeof FileDto>;

export type DownloadFiles = z.infer<typeof Files>;

function isFilesData(data: unknown): data is DownloadFiles {
  return Files.safeParse(data).success;
}

export interface IFileDownloadCellRendererParams<
  TData = unknown,
> extends ICellRendererParams<TData, DownloadFiles> {
  onFileClick?: (file: FileDto) => void;
}

export const FileDownloadCellRenderer = ({
  formatValue,
  onFileClick,
  value,
}: IFileDownloadCellRendererParams) => {
  const { t } = useTranslation();

  if (value === undefined) {
    return;
  }

  if (value == null || (Array.isArray(value) && value.length === 0)) {
    return formatValue?.(value) ?? EMPTY_VALUE;
  }

  if (!isFilesData(value)) {
    throw new Error(
      `FileDownloadCellRenderer called with wrong data format: ${JSON.stringify(value, null, 2)}`
    );
  }

  return (
    <div className={styles['root']}>
      {value?.map((file) => {
        const { id, fileName, mimetype, virusFree } = file;
        const virusInfected = virusFree === false;
        const allowFileDownload = onFileClick && !virusInfected;

        return (
          <div
            {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
            data-testid={`file-download-cell-renderer-file_${id}`}
            onClick={allowFileDownload ? () => onFileClick(file) : undefined}
            key={id}
          >
            {virusInfected ? (
              <TextWithTooltip
                tooltipProps={{
                  disabled: false,
                  label: t('app.table.tooltip.maliciousContent'),
                }}
                style={{ display: 'flex' }}
              >
                <SVGIcon name="file-alert" />
              </TextWithTooltip>
            ) : (
              <TextWithTooltip
                tooltipProps={{ disabled: false, label: fileName }}
                style={{ cursor: 'pointer', display: 'flex' }}
              >
                <SVGIcon
                  name={getSVGIconNameFromDocument({ fileName, mimetype })}
                />
              </TextWithTooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};
